<template>
    <inline-tab-panel
        name="attachments"
        v-bind="$attrs"
        class="attachments-tab-panel"
        with-variable-height
    >
        <flex-container
            v-if="isUserOfAssignedCompany && createDocumentOptions.length > 0"
            gap="0"
            align-content="center"
            justify-content="center"
        >
            <button-collapse
                :options="createDocumentOptions"
                variant="text"
                horizontal-position="center"
                @click="createDocument"
            >
                <template #default>
                    <svg-icon :icon="IconAdd" />
                    Document genereren
                </template>
            </button-collapse>

            <horizontal-line />
        </flex-container>

        <input-group v-if="isUserOfAssignedCompany" :model-value="[]">
            <template #input>
                <file-drop-zone-input
                    multiple
                    direct-upload
                    :direct-upload-callback="uploadFile"
                />
            </template>
        </input-group>

        <template
            v-for="attachment in availableAttachments"
            :key="attachment.id.toString()"
        >
            <horizontal-line />

            <div class="attachments-tab-panel__attachment">
                <div class="attachments-tab-panel__attachment__text">
                    <div>
                        <div style="rotate: 45deg; display: inline-block;">
                            <svg-icon :icon="IconAttachFile" />
                        </div>
                        <div>
                            <link-decorator
                                :href="attachment.fileName"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {{ attachment.name }}
                            </link-decorator>
                        </div>
                    </div>
                    <div>
                        <text-decorator :color="Color.gray400">
                            {{ formatFileSize(attachment.fileSize) }}
                            &bull;
                            {{ formatDate(attachment.createdAt) }}
                        </text-decorator>
                    </div>
                    <div>
                        {{ attachment.createdBy ? attachment.createdBy.name : 'KeyVue' }}
                    </div>
                </div>

                <div class="attachments-tab-panel__attachment__icons">
                    <button-block-icon
                        :icon="IconDownload"
                        @click="browserDownload(attachment.fileName, attachment.name)"
                    >
                        Downloaden
                    </button-block-icon>
                    <button-outline-icon
                        v-if="canDelete"
                        :icon="IconDelete"
                        :color="Color.red500"
                        @click="deleteAttachment(attachment.id)"
                    >
                        Verwijderen
                    </button-outline-icon>
                </div>
            </div>
        </template>

        <div v-if="availableAttachments.length === 0" class="attachments-tab-panel__empty">
            Er zijn geen bestanden voor deze lead.
        </div>
    </inline-tab-panel>
</template>

<script setup lang="ts">
    import {
        ButtonBlockIcon,
        ButtonCollapse,
        type ButtonCollapseOption,
        ButtonOutlineIcon,
        Color,
        HorizontalLine,
        InlineTabPanel,
        LinkDecorator,
        SvgIcon,
        TextDecorator,
        InputGroup,
        FileDropZoneInput,
    } from '@keyvue/one-ui';

    import IconAdd from '@keyvue/one-material-icons/icons/add.svg?component';
    import IconAttachFile from '@keyvue/one-material-icons/icons/attach_file.svg?component';
    import IconDownload from '@keyvue/one-material-icons/icons/download.svg?component';
    import IconDelete from '@keyvue/one-material-icons/icons/delete.svg?component';
    import { formatDate } from '@/utils/date';
    import { browserDownload, formatFileSize } from '@/utils/file';
    import { computed } from 'vue';
    import { useRouter } from 'vue-router';
    import { type Attachment, AttachmentId, DocumentTemplate, DocumentType, type Lead } from '@keyvue/one-api-client';
    import useClient, { type ReactiveCollection } from '@composables/useClient';
    import useLeadSecurity from '@composables/security/useLeadSecurity';
    import { AttachmentAPI, ReferralAPI } from '@keyvue/one-api-client-legacy';
    import { v4 as uuid } from 'uuid';
    import useEventBus from '@composables/useEventBus';
    import useLeadAttachmentSecurity from '@composables/security/useLeadAttachmentSecurity';
    import useNotify from '@composables/useNotify';

    const router = useRouter();
    const { getLazyReactiveCollection } = useClient();
    const { notifySaving, notifySaved } = useNotify();
    const eventBus = useEventBus();

    export interface Props {
        lead: Lead,
        attachmentCollection: ReactiveCollection<Attachment>,
    }

    const props = defineProps<Props>();
    const { isUserOfAssignedCompany } = await useLeadSecurity(props.lead);
    const { canDelete } = await useLeadAttachmentSecurity(props.lead);

    const availableAttachments = computed(() => props.attachmentCollection.data.value);

    const documentTemplateCollection = getLazyReactiveCollection(DocumentTemplate, { active: true });
    documentTemplateCollection.load();

    const createDocumentOptions = computed(() => {
        const result: ButtonCollapseOption[] = [];

        DocumentType.values.forEach((documentType) => {
            result.push({
                value: documentType.value,
                label: documentType.label,
                disabled: !documentTemplateCollection.data.value.some((template) => template.layout.type.equals(documentType)),
            });
        });

        return result;
    });

    const uploadFile = async (uploadableFiles: File[]) => {
        const legacyReferral = await ReferralAPI.get(props.lead.id.value);
        const promises: Promise<any>[] = [];

        for (let i = 0; i < uploadableFiles.length; i += 1) {
            const loopFile = uploadableFiles[i];

            promises.push(AttachmentAPI.createWithFile(
                legacyReferral,
                uuid(),
                loopFile,
            ));
        }

        await Promise.all(promises);
        eventBus.dispatch('resource-manipulated', 'Attachment');
    };

    const createDocument = (documentType: string) => {
        router.push({
            name: 'documentGenerate',
            params: {
                type: documentType,
                lead: props.lead.id.toString(),
            },
        });
    };

    const deleteAttachment = async (attachmentId: AttachmentId) => {
        const closeNotification = notifySaving('We zijn het bestand aan het verwijderen. Even geduld alstublieft.', 0);
        await AttachmentAPI.delete(attachmentId.toString());
        eventBus.dispatch('resource-manipulated', 'Attachment');
        notifySaved('Het bestand is succesvol verwijderd!');
        if (closeNotification) {
            closeNotification();
        }
    };
</script>

<style scoped lang="scss">
.attachments-tab-panel {
    padding: size(md) !important;

    &__attachment {
        background-color: color(gray-200);
        padding: size(md);
        border-radius: size(sm);
        display: flex;
        flex-direction: row;
        gap: size(sm);
        margin-bottom: size(lg);

        &__icons,
        &__text {
            display: flex;
            flex-direction: column;
            justify-content: center;
            justify-items: center;

            height: 100%;
        }

        &__text {
            gap: size(xs);
            flex: 1;

            > div {
                display: flex;
                align-content: center;
                align-items: center;
                gap: size(xs);
            }
        }

        &__icons {
            gap: size(xs);
        }
    }

    &__empty {
        margin-top: size(lg);
    }
}
</style>
